import { useState } from "react";
import { useParams } from "react-router-dom";
import { Table, Button, Modal, Form, Input, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEpics, useCreateEpic } from "../../queries/epics";
import { useProject } from "../../queries/projects";
import { EpicProps, EpicStatus } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { ProjectNav } from "../../components/ui";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { EpicStatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";

export const EpicListPage = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { data: epics, isLoading, isError, error } = useEpics(projectId!);
  const { data: project } = useProject(projectId!);
  const createEpic = useCreateEpic(projectId!);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async () => {
    const values = await form.validateFields();
    try {
      await createEpic.mutateAsync(values);
      setIsModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the epic");
    }
  };

  const open = (epics || []).filter((e: EpicProps) => e.status !== "done").length;

  return (
    <PageShell
      kind="list"
      crumbs={[{ label: "Projects", to: "/projects" }, { label: project?.name || "Project" }]}
      title="Epics"
      subtitle="The large threads of work this project breaks down into."
      meta={[
        { label: "Total", value: <span className="q-num">{(epics || []).length}</span> },
        { label: "Open", value: <span className="q-num">{open}</span> },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>New epic</Button>}
      tabs={<ProjectNav projectId={projectId!} active="epics" />}
    >
      <Toolbar count={(epics || []).length} unit="epics" />
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<EpicProps>
          rowKey="_id"
          size="small"
          dataSource={epics || []}
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState
                title="No epics yet"
                description="Epics group related tasks so the board and the backlog stay readable once the project grows."
                actions={<Button type="primary" onClick={() => setIsModalOpen(true)}>New epic</Button>}
              />
            ),
          }}
          columns={[
            { title: "Title", dataIndex: "title", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
            {
              title: "Description", dataIndex: "description",
              render: (v?: string) => v
                ? <span style={{ color: "var(--q-text-muted)" }}>{v}</span>
                : <span style={{ color: "var(--q-text-faint)" }}>—</span>,
            },
            { title: "Status", dataIndex: "status", width: 140, render: (s: EpicStatus) => <EpicStatusTag status={s} /> },
            {
              title: "Target", dataIndex: "targetDate", width: 130, align: "right",
              render: (v?: string) => v
                ? <span className="q-num">{dayjs(v).format("D MMM YYYY")}</span>
                : <span style={{ color: "var(--q-text-faint)" }}>—</span>,
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title="New epic"
        open={isModalOpen}
        onOk={handleCreate}
        okText="Create epic"
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={createEpic.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
